/**
 * Fixtures Transform Module
 *
 * Handles data transformation and computed properties for fixtures
 */

/**
 * Convert UTC datetime to user's local time
 *
 * @param {string} utcDatetime - UTC datetime string (e.g., "2025-07-30T16:00:00")
 * @return {string|null} Formatted local time or null if invalid
 */
function convertUtcToLocalTime(utcDatetime) {
  if (!utcDatetime) {
    return null;
  }

  try {
    const utcDate = new Date(utcDatetime);

    if (isNaN(utcDate.getTime())) {
      return null;
    }

    return utcDate.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch (error) {
    return null;
  }
}

/**
 * Add computed properties to a single fixture
 *
 * @param {Object} fixture  Fixture data
 * @param {string} basePath Base path for match URLs
 * @return {Object} Fixture with computed properties
 */
export function addComputedPropertiesToFixture(fixture, basePath) {
  const hasScore =
    fixture.home_score !== null &&
    fixture.home_score !== undefined &&
    fixture.away_score !== null &&
    fixture.away_score !== undefined;

  const competition = fixture.competition || '';
  const compSlug = competition.toLowerCase().replace(/\s+/g, '-');

  // Convert UTC time to user's local timezone
  let displayTime = fixture.time || '';
  if (fixture.utc_datetime) {
    const convertedTime = convertUtcToLocalTime(fixture.utc_datetime);
    displayTime = convertedTime || displayTime;
  }

  return {
    ...fixture,
    hasScore,
    scoreText: hasScore ? `${fixture.home_score} - ${fixture.away_score}` : '',
    displayTime,
    matchUrl: fixture.id ? `/${basePath}/${fixture.id}` : '',
    competitionSlug: compSlug,
  };
}

/**
 * Group fixtures by competition
 *
 * Prepares fixture data by:
 * - Grouping fixtures by competition
 * - Adding computed boolean properties (hasScore, isLive)
 * - Pre-calculating display values (scoreText, matchUrl, competitionSlug)
 * - Creating flattened fixtures map by ID for reactive updates
 *
 * @param {Array}  fixtures Array of fixtures
 * @param {string} basePath Base path for match URLs
 * @return {Object} Object with fixturesById and competitionGroups
 */
export function groupFixturesByCompetition(fixtures, basePath) {
  if (!fixtures || !Array.isArray(fixtures)) {
    return { fixturesById: {}, competitionGroups: [] };
  }

  const grouped = {};
  const fixturesById = {};

  fixtures.forEach((fixture) => {
    const compName = fixture.competition || 'Other';
    if (!grouped[compName]) {
      grouped[compName] = [];
    }

    // Add computed properties to fixture
    const processedFixture = addComputedPropertiesToFixture(fixture, basePath);

    // Store in flattened map by ID for reactive state
    if (processedFixture.id) {
      fixturesById[processedFixture.id] = processedFixture;
    }

    // Store full fixture in grouped structure (for initial render)
    grouped[compName].push(processedFixture);
  });

  const competitionGroups = [];
  Object.entries(grouped).forEach(([compName, fixturesInComp]) => {
    competitionGroups.push({
      name: compName,
      fixtures: fixturesInComp, // Full fixture data for context
    });
  });

  return { fixturesById, competitionGroups };
}

/**
 * Transform API response to grouped competition data
 *
 * Extracts fixtures from API response and transforms them into
 * competition groups with computed properties and a flattened fixtures map
 *
 * @param {Array}  apiResponse Raw API response (array of day objects)
 * @param {string} basePath    Base path for match URLs
 * @return {Object} Object with date, date_display, fixturesById, and competitionGroups
 */
export function transformApiResponse(apiResponse, basePath) {
  const dayData = apiResponse[0] || {};
  const fixturesList = dayData.fixtures || [];
  const { fixturesById, competitionGroups } = groupFixturesByCompetition(fixturesList, basePath);

  return {
    date: dayData.date || '',
    date_display: dayData.date_display || '',
    fixturesById,
    competitionGroups,
  };
}

/**
 * Check if any fixtures are live
 *
 * @param {Object} fixturesById - Map of fixture IDs to fixture objects
 * @return {boolean} True if any fixtures are live
 */
export function hasLiveFixtures(fixturesById) {
  if (!fixturesById || typeof fixturesById !== 'object') {
    return false;
  }

  return Object.values(fixturesById).some((fixture) => {
    const status = fixture?.status || '';
    return status.toLowerCase() === 'live';
  });
}
